import { Col, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { faTurnDownRight } from '@fortawesome/pro-solid-svg-icons'
import { clsx } from 'clsx'

import { Badge } from 'Components/_theme'
import { FormattedCurrency } from 'Components/FormattedCurrency/FormattedCurrency'
import { Icon } from 'Components/Icon/Icon'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'

import { ImagePlaceholderTypesEnum } from 'Types/imagePlaceholders.type'

import type { OrderDevice } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'
import type { OrderLimitations } from 'Portal/src/stores/orderStore/orderStore.types'

import type {
  OrderItemTextField,
  OrderSummaryDeviceRowRendererProps,
} from '../OrderSummary.types'
import { OrderSummaryQuantityInput } from './OrderSummaryQuantityInput'
import { OrderSummaryTextField } from './OrderSummaryTextField'

type OrderSummaryDeviceRowProps = OrderSummaryDeviceRowRendererProps & {
  isOrganisation?: boolean
  isNewOrder?: boolean
  onSetChosenDeviceTextField: (key: OrderItemTextField, value: string) => void
  onSetAddonDeviceTextField: (
    key: OrderItemTextField,
    value: string,
    addonDevice?: OrderDevice['id'],
  ) => void
  onSetChosenDeviceQuantity: (quantity: number) => void
  enableCustomerLabels: boolean
  enablePoRcnInOrderSummary: boolean
  enableClientReferenceInOrderSummary: boolean
  enableBillingIdentifierInOrderSummary: boolean
  enableChosenDeviceQuantityLimitations?: boolean
  limitationRules: OrderLimitations | null
}

export const OrderSummaryDeviceRow = (props: OrderSummaryDeviceRowProps) => {
  const {
    isAddon,
    isBundleRow,
    isBundleItemRow,
    isCreateSeparateRental,
    deviceName,
    deviceImage,
    deviceQuantity,
    deviceRentalDuration,
    deviceId,
    devicePrice,
    deviceCustomerLabel,
    deviceType,
    poRcn,
    customClientReference,
    billingIdentifier,
    isPurchase,
    isOrganisation,
    isNewOrder,
    onSetAddonDeviceTextField,
    onSetChosenDeviceQuantity,
    onSetChosenDeviceTextField,
    enableCustomerLabels,
    enablePoRcnInOrderSummary,
    enableClientReferenceInOrderSummary,
    enableBillingIdentifierInOrderSummary,
    enableChosenDeviceQuantityLimitations,
    limitationRules,
  } = props

  const showCustomerLabel =
    isOrganisation && enableCustomerLabels && !isBundleRow
  const showPoRcn = isOrganisation && enablePoRcnInOrderSummary && !isBundleRow
  const showClientReference =
    isOrganisation && enableClientReferenceInOrderSummary && !isBundleRow
  const showBillingIdentifier =
    isOrganisation && enableBillingIdentifierInOrderSummary && !isBundleRow

  const hideDurationAndPrice = isPurchase
    ? !isOrganisation
    : isAddon && !isCreateSeparateRental

  const quantityInputMaxQuantityValue =
    (limitationRules && limitationRules[deviceType as keyof OrderLimitations]
      ? limitationRules[deviceType as keyof OrderLimitations].current || 0
      : (limitationRules && limitationRules.other.current) || 0) + 1

  return (
    <Row
      key={deviceId}
      className={clsx(
        'px-4 py-6 device-overview',
        isNewOrder && !isOrganisation && 'justify-content-between',
      )}
    >
      <Col
        className={clsx(isBundleItemRow && 'd-flex')}
        xs={isNewOrder ? 6 : 8}
      >
        {isBundleItemRow && (
          <Icon className="me-2" icon={faTurnDownRight} size={20} />
        )}
        <div className="d-flex align-items-center">
          <ImageWithFallback
            alt={deviceName || ''}
            placeholderType={ImagePlaceholderTypesEnum.newOrderFlow}
            src={deviceImage || ''}
          />

          <div className="ms-2 ms-md-6">
            <strong>{deviceName}</strong>

            <br />

            {!isBundleRow && isPurchase && (
              <Badge
                className="mt-4"
                color="yellow"
                rounded
                variant="secondary"
              >
                <FormattedMessage id="NewOrder_purchase" />
              </Badge>
            )}
          </div>
        </div>
      </Col>

      <Col className="d-flex justify-content-end align-items-center" xs={2}>
        {!isBundleRow && isPurchase
          ? '-'
          : !isBundleRow &&
            !hideDurationAndPrice && (
              <>
                {deviceRentalDuration}&nbsp;
                <FormattedMessage id="NewOrderSummary_months" />
              </>
            )}
      </Col>

      {isNewOrder && isOrganisation && (
        <Col className="d-flex justify-content-end align-items-center" xs={2}>
          {isBundleItemRow || hideDurationAndPrice ? (
            deviceQuantity
          ) : (
            <OrderSummaryQuantityInput
              controlId={`deviceQuantity-${deviceId}`}
              defaultValue={deviceQuantity}
              maxQuantity={
                enableChosenDeviceQuantityLimitations
                  ? quantityInputMaxQuantityValue
                  : undefined
              }
              onChangeInput={value => {
                onSetChosenDeviceQuantity(value)
              }}
            />
          )}
        </Col>
      )}

      {(!hideDurationAndPrice || isPurchase) && (
        <Col className="d-flex justify-content-end align-items-center" xs={2}>
          {!isBundleRow && <FormattedCurrency value={devicePrice} />}
        </Col>
      )}

      {showCustomerLabel && (
        <Col className="ps-0" md={8} xs={12}>
          <OrderSummaryTextField
            controlId={`customDeviceLabel-${deviceId}`}
            defaultValue={deviceCustomerLabel || ''}
            onChangeLabel={event =>
              isAddon
                ? onSetAddonDeviceTextField(
                    'customerLabel',
                    event.currentTarget.value,
                    deviceId,
                  )
                : onSetChosenDeviceTextField(
                    'customerLabel',
                    event.currentTarget.value,
                  )
            }
            placeholderTranslationId="NewOrderSummary_label_placeholder"
          />
        </Col>
      )}

      {showPoRcn && (
        <Col className="ps-0" md={8} xs={12}>
          <OrderSummaryTextField
            controlId={`poRcn-${deviceId}`}
            defaultValue={poRcn || ''}
            onChangeLabel={event =>
              isAddon
                ? onSetAddonDeviceTextField(
                    'poRcn',
                    event.currentTarget.value,
                    deviceId,
                  )
                : onSetChosenDeviceTextField('poRcn', event.currentTarget.value)
            }
            placeholderTranslationId="NewOrderSummary_po_rcn_placeholder"
          />
        </Col>
      )}

      {showClientReference && (
        <Col className="ps-0" md={8} xs={12}>
          <OrderSummaryTextField
            controlId={`customClientReference-${deviceId}`}
            defaultValue={customClientReference || ''}
            onChangeLabel={event =>
              isAddon
                ? onSetAddonDeviceTextField(
                    'customClientReference',
                    event.currentTarget.value,
                    deviceId,
                  )
                : onSetChosenDeviceTextField(
                    'customClientReference',
                    event.currentTarget.value,
                  )
            }
            placeholderTranslationId="NewOrderSummary_custom_client_reference_placeholder"
          />
        </Col>
      )}

      {showBillingIdentifier && (
        <Col className="ps-0" md={8} xs={12}>
          <OrderSummaryTextField
            controlId={`billingIdentifier-${deviceId}`}
            defaultValue={billingIdentifier || ''}
            onChangeLabel={event =>
              isAddon
                ? onSetAddonDeviceTextField(
                    'billingIdentifier',
                    event.currentTarget.value,
                    deviceId,
                  )
                : onSetChosenDeviceTextField(
                    'billingIdentifier',
                    event.currentTarget.value,
                  )
            }
            placeholderTranslationId="NewOrderSummary_billing_identifier_placeholder"
          />
        </Col>
      )}
    </Row>
  )
}
